// As unidades do SKU e as contas de conversão entre elas.
//
// Por que isto não mora dentro do JSX: a linha viva da conversão é o que prova,
// na tela, que os fatores digitados fazem sentido ("1 caixa de 12 = 12 un =
// 3,600 kg"). Conta escrita no meio do render não se testa, não se reusa nos
// dois modais e vira duas versões que discordam na primeira mudança.
//
// Tudo entra como STRING de propósito: `<input type="number">` devolve string,
// e campo vazio ("") precisa ser distinguível de zero — `Number("")` é 0, e um
// fator 0 exibido como número mente muito mais alto do que a frase "falta o
// fator". Nenhuma função daqui devolve NaN: ou devolve número válido, ou
// devolve null e quem chama diz o que está faltando.

export interface OpcaoDeUnidade {
  valor: string;
  rotulo: string;
  /**
   * Quantas peças a embalagem tem quando o PRÓPRIO NOME já diz (caixa de 12).
   * Só existe onde é fato da embalagem — "fardo" e "galão" não têm contagem
   * fixa, e chutar uma aqui seria inventar dado de estoque alheio.
   */
  pecas?: number;
}

/** Valor do item "outra" do select de compra: libera o campo de texto livre. */
export const VALOR_OUTRA_UNIDADE_DE_COMPRA = "outra";

export const UNIDADES_DE_COMPRA: OpcaoDeUnidade[] = [
  { valor: "caixa-12", rotulo: "caixa de 12", pecas: 12 },
  { valor: "caixa-24", rotulo: "caixa de 24", pecas: 24 },
  { valor: "fardo", rotulo: "fardo" },
  { valor: "saco-25kg", rotulo: "saco de 25 kg" },
  { valor: "saco-5kg", rotulo: "saco de 5 kg" },
  { valor: "galao", rotulo: "galão" },
  { valor: "unidade", rotulo: "unidade", pecas: 1 },
  { valor: VALOR_OUTRA_UNIDADE_DE_COMPRA, rotulo: "outra (descrever)" },
];

/** O que o estoque conta. Massa, volume ou peça — nada além disso. */
export const UNIDADES_DE_ESTOQUE: OpcaoDeUnidade[] = [
  { valor: "kg", rotulo: "kg" },
  { valor: "L", rotulo: "L" },
  { valor: "un", rotulo: "un" },
];

/** O que a receita/ficha técnica consome, sempre o submúltiplo do estoque. */
export const UNIDADES_DE_CONSUMO: OpcaoDeUnidade[] = [
  { valor: "g", rotulo: "g" },
  { valor: "ml", rotulo: "ml" },
  { valor: "un", rotulo: "un" },
];

/**
 * Quanto de consumo cabe em 1 de estoque.
 *
 * O par é fechado por natureza da grandeza: massa vira massa, volume vira
 * volume. kg -> ml não é conversão, é erro de cadastro — devolve null para a
 * tela poder dizer isso em vez de multiplicar por um número qualquer.
 */
export function fatorEstoqueParaConsumo(
  unidadeEstoque: string,
  unidadeConsumo: string
): number | null {
  if (unidadeEstoque === "kg" && unidadeConsumo === "g") return 1000;
  if (unidadeEstoque === "L" && unidadeConsumo === "ml") return 1000;
  if (unidadeEstoque === "un" && unidadeConsumo === "un") return 1;
  return null;
}

/** A unidade de consumo que combina com cada estoque, para o texto da recusa. */
export function consumoEsperadoPara(unidadeEstoque: string): string | null {
  const esperado = UNIDADES_DE_CONSUMO.find(
    (opcao) => fatorEstoqueParaConsumo(unidadeEstoque, opcao.valor) !== null
  );
  return esperado ? esperado.valor : null;
}

/**
 * String de campo numérico -> número, ou null.
 *
 * Aceita vírgula porque teclado numérico de pt-BR manda vírgula em colagem, e
 * `Number("0,7")` é NaN — o NaN só apareceria depois, na multiplicação, sem
 * ninguém saber de onde veio.
 */
export function paraNumero(texto: string | number | null | undefined): number | null {
  if (texto === null || texto === undefined) return null;
  const cru = String(texto).trim();
  if (cru === "") return null;
  const numero = Number(cru.replace(",", "."));
  return Number.isFinite(numero) ? numero : null;
}

/** Quantidade com 3 casas, sempre: "3,600 kg" e não "3,6 kg". */
export function formatarQuantidade(valor: number): string {
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(valor);
}

/** Fator inteiro ou quase: 1000 -> "1.000", 12 -> "12". */
export function formatarFator(valor: number): string {
  return new Intl.NumberFormat("pt-BR", {
    maximumFractionDigits: 3,
  }).format(valor);
}

export function unidadeDeCompraPorValor(valor: string): OpcaoDeUnidade | undefined {
  return UNIDADES_DE_COMPRA.find((opcao) => opcao.valor === valor);
}

/**
 * Como a unidade de compra se chama na frase. Em "outra", quem manda é o texto
 * que o usuário escreveu — se ele não escreveu nada, não há rótulo (null), e a
 * linha cobra o preenchimento em vez de exibir "1 outra (descrever) = ...".
 */
export function rotuloDaUnidadeDeCompra(
  valor: string,
  textoLivre: string
): string | null {
  if (valor === VALOR_OUTRA_UNIDADE_DE_COMPRA) {
    const escrito = (textoLivre || "").trim();
    return escrito === "" ? null : escrito;
  }
  const opcao = unidadeDeCompraPorValor(valor);
  return opcao ? opcao.rotulo : null;
}

/**
 * O rendimento é uma FRAÇÃO do que se comprou: 1 kg de alface dá 0,7 kg limpo.
 * Acima de 1 seria matéria-prima nascendo na limpeza; 0 ou negativo seria
 * estoque que desaparece. Os dois casos são recusa, não arredondamento.
 */
export const RENDIMENTO_INVALIDO =
  "O fator de rendimento tem de ser maior que 0 e menor ou igual a 1 " +
  "(1 kg de alface que dá 0,7 kg limpo é 0,7). Corrija antes de salvar.";

export function rendimentoEhValido(valor: number | null): boolean {
  return valor !== null && valor > 0 && valor <= 1;
}

export interface EntradaDeConversao {
  unidadeCompra: string;
  unidadeCompraOutra: string;
  unidadeEstoque: string;
  unidadeConsumo: string;
  fatorConversaoCompraEstoque: string;
  fatorRendimento: string;
}

export interface LinhaDeConversao {
  /** Todos os fatores presentes e coerentes — nada faltando na frase. */
  completa: boolean;
  texto: string;
}

/** "a, b e c" — para a frase do que está faltando não sair com vírgula solta. */
function listar(itens: string[]): string {
  if (itens.length === 1) return itens[0];
  return `${itens.slice(0, -1).join(", ")} e ${itens[itens.length - 1]}`;
}

/**
 * A linha viva, recalculada a cada digitação:
 *
 *   1 caixa de 12 = 12 un = 3,600 kg · rendimento 0,700 -> 2,520 kg
 *   aproveitados · 1 kg = 1.000 g
 *
 * O que não dá para calcular não vira NaN nem some: vira o nome do que falta,
 * porque um cadastro de estoque pela metade só se descobre no inventário.
 */
export function montarLinhaDeConversao(
  entrada: EntradaDeConversao
): LinhaDeConversao {
  const partes: string[] = [];
  const faltas: string[] = [];

  const rotuloCompra = rotuloDaUnidadeDeCompra(
    entrada.unidadeCompra,
    entrada.unidadeCompraOutra
  );
  const estoque = entrada.unidadeEstoque;
  const consumo = entrada.unidadeConsumo;
  const fator = paraNumero(entrada.fatorConversaoCompraEstoque);
  const rendimento = paraNumero(entrada.fatorRendimento);

  if (!rotuloCompra) {
    faltas.push(
      entrada.unidadeCompra === VALOR_OUTRA_UNIDADE_DE_COMPRA
        ? "o nome da outra unidade de compra"
        : "a unidade de compra"
    );
  }
  if (!estoque) faltas.push("a unidade de estoque");
  if (fator === null) {
    faltas.push(
      `o fator de conversão (quanto de ${estoque || "estoque"} vem em 1 ${
        rotuloCompra || "unidade de compra"
      })`
    );
  } else if (fator <= 0) {
    faltas.push("um fator de conversão maior que zero");
  }

  if (rotuloCompra && estoque && fator !== null && fator > 0) {
    const pecas = unidadeDeCompraPorValor(entrada.unidadeCompra)?.pecas;
    const trechoPecas = pecas ? ` = ${formatarFator(pecas)} un` : "";
    partes.push(
      `1 ${rotuloCompra}${trechoPecas} = ${formatarQuantidade(fator)} ${estoque}`
    );

    if (rendimento === null) {
      faltas.push("o fator de rendimento");
    } else if (!rendimentoEhValido(rendimento)) {
      faltas.push("um rendimento maior que 0 e menor ou igual a 1");
    } else {
      partes.push(
        `rendimento ${formatarQuantidade(rendimento)} -> ${formatarQuantidade(
          fator * rendimento
        )} ${estoque} aproveitados`
      );
    }
  }

  if (!consumo) {
    faltas.push("a unidade de consumo");
  } else if (estoque) {
    const fatorConsumo = fatorEstoqueParaConsumo(estoque, consumo);
    if (fatorConsumo === null) {
      const esperado = consumoEsperadoPara(estoque);
      faltas.push(
        `uma unidade de consumo compatível com ${estoque}` +
          (esperado ? ` (é ${esperado})` : "")
      );
    } else {
      partes.push(`1 ${estoque} = ${formatarFator(fatorConsumo)} ${consumo}`);
    }
  }

  if (partes.length === 0) {
    return { completa: false, texto: `Falta ${listar(faltas)}.` };
  }

  const frase = partes.join("  ·  ");
  return {
    completa: faltas.length === 0,
    texto: faltas.length === 0 ? frase : `${frase}  ·  falta ${listar(faltas)}`,
  };
}
